import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ChooseFromBoardPanel from "@/components/ChooseFromBoardPanel";
import {
  PhotoCard, NoteCard, ProCard, AddPhotoDialog, NoteDialog, CommentThread, NotesSidebar, ImageLightbox,
} from "@/components/ProjectBoardParts";
import {
  X, Plus, Trash2, Archive, RotateCcw, Globe2, Share2, LayoutGrid, MessageSquare, Info, Pencil,
} from "lucide-react";

const CLOSE_REASONS = [["completed", "Completed"], ["paused", "Paused"], ["cancelled", "Cancelled"], ["no_longer_needed", "No longer needed"]];
const PERMISSIONS = [
  ["view", "View", "Can view this project."],
  ["comment", "Comment", "Can view and join the conversation."],
  ["contribute", "Collaborate", "Can add Pros, notes, photos and comments. You remain the project owner."],
];
const PERMISSION_LABEL = { view: "View only", comment: "Can comment", contribute: "Can collaborate" };

const relTime = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  return new Date(iso).toLocaleDateString();
};

export default function ProjectDetailModal({ projectId, initialAction, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialAction === "sharing" ? "sharing" : initialAction === "publish" ? "publish" : "board");
  const [showChoose, setShowChoose] = useState(false);
  const [showClose, setShowClose] = useState(initialAction === "close");
  const [closeReason, setCloseReason] = useState("completed");
  const [addPhoto, setAddPhoto] = useState(false);
  const [noteDialog, setNoteDialog] = useState(null);   // {} = new, note = edit
  const [thread, setThread] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [editDetails, setEditDetails] = useState(false);
  const [form, setForm] = useState({});
  const [connections, setConnections] = useState([]);
  const [shareClient, setShareClient] = useState("");
  const [sharePerm, setSharePerm] = useState("view");
  const [publishDraft, setPublishDraft] = useState(null);
  const [publishForm, setPublishForm] = useState({ title: "", description: "", pro_ids: [], media_ids: [] });

  const load = () => api.get(`/projects/${projectId}`).then(r => setData(r.data)).catch(() => toast.error("Could not load project"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => {
    if (tab === "sharing") api.get("/client-connections/mine").then(r => setConnections(r.data.connections || [])).catch(() => {});
    if (tab === "publish") {
      api.get(`/projects/${projectId}/publish`).then(r => {
        setPublishDraft(r.data);
        const h = r.data.highlight;
        setPublishForm(h ? { title: h.title, description: h.description || "", pro_ids: h.pro_ids, media_ids: h.media_ids } : { title: "", description: "", pro_ids: [], media_ids: [] });
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [tab, projectId]);

  if (!data) return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 text-sm text-[#222]/50">Loading…</div>
    </div>
  );

  const { project, my_permission, pros, notes, media, shares, comments } = data;
  const isOwner = my_permission === "owner";
  const canContribute = isOwner || my_permission === "contribute";
  const canComment = canContribute || my_permission === "comment";
  const photoLimit = data.photo_limit || 12;

  const pinnedNotes = notes.filter(n => n.pinned);
  const sidebarNotes = notes.filter(n => !n.pinned);
  const itemComments = (type, id) => comments.filter(c => c.item_type === type && c.item_id === id);
  const projectComments = comments.filter(c => !c.item_type);
  const itemCount = media.length + pros.length + pinnedNotes.length;

  const refresh = () => { load(); onChanged(); };
  const guard = (fn, msg) => async (...a) => { try { await fn(...a); refresh(); } catch (e) { toast.error(e.response?.data?.detail || msg); } };

  const setProStatus = guard((id, status) => api.patch(`/projects/${projectId}/pros/${id}`, { status }), "Could not update status");
  const removePro = async (id) => {
    if (!window.confirm("Remove this pro from the project? They stay pinned on your My Board.")) return;
    guard(() => api.delete(`/projects/${projectId}/pros/${id}`), "Could not remove")();
  };
  const editProNote = (m) => {
    const note = window.prompt("Private note about this pro (only you can see it):", m.note || "");
    if (note === null) return;
    guard(() => api.patch(`/projects/${projectId}/pros/${m.pro_user_id}`, { note }), "Could not save note")();
  };

  const savePhoto = guard(async (payload) => { await api.post(`/projects/${projectId}/media`, payload); setAddPhoto(false); }, "Could not add photo");
  const deleteMedia = (id) => {
    if (!window.confirm("Remove this photo from the Project Board?")) return;
    guard(() => api.delete(`/projects/${projectId}/media/${id}`), "Could not delete")();
  };
  const editCaption = (m) => {
    const caption = window.prompt("Caption for this photo:", m.caption || "");
    if (caption === null) return;
    guard(() => api.patch(`/projects/${projectId}/media/${m.id}`, { caption }), "Could not save caption")();
  };

  const saveNote = guard(async (payload) => {
    if (noteDialog?.id) await api.patch(`/projects/${projectId}/notes/${noteDialog.id}`, payload);
    else await api.post(`/projects/${projectId}/notes`, payload);
    setNoteDialog(null);
  }, "Could not save note");
  const unpinNote = guard((n) => api.patch(`/projects/${projectId}/notes/${n.id}`, { text: n.text, pinned: false }), "Could not unpin");
  const deleteNote = guard((id) => api.delete(`/projects/${projectId}/notes/${id}`), "Could not delete");

  const postComment = guard(async (text) => {
    const body = { text };
    if (thread && thread._t) { body.item_type = thread._t; body.item_id = thread.id; }
    await api.post(`/projects/${projectId}/comments`, body);
  }, "Could not post comment");

  const saveDetails = guard(async () => { await api.patch(`/projects/${projectId}`, form); setEditDetails(false); }, "Could not save");

  const addShare = guard(async () => { await api.post(`/projects/${projectId}/shares`, { client_id: shareClient, permission: sharePerm }); setShareClient(""); toast.success("Shared"); }, "Could not share — must be an accepted Connection");
  const updateSharePerm = guard((id, permission) => api.patch(`/projects/${projectId}/shares/${id}`, { permission }), "Could not update");
  const revokeShare = guard((id) => api.delete(`/projects/${projectId}/shares/${id}`), "Could not revoke");

  const doClose = guard(async () => { await api.post(`/projects/${projectId}/close`, { reason: closeReason }); setShowClose(false); toast.success("Moved to Filing Cabinet"); }, "Could not close");
  const doReopen = guard(() => api.post(`/projects/${projectId}/reopen`), "Could not reopen");

  const togglePub = (k, id) => setPublishForm(f => ({ ...f, [k]: f[k].includes(id) ? f[k].filter(x => x !== id) : [...f[k], id] }));
  const doPublish = guard(async () => {
    if (!publishForm.title.trim()) throw { response: { data: { detail: "Add a title" } } };
    await api.post(`/projects/${projectId}/publish`, publishForm); toast.success("Published to your profile");
  }, "Could not publish");
  const doUnpublish = async () => {
    if (!window.confirm("Remove this project from your public profile?")) return;
    guard(() => api.delete(`/projects/${projectId}/publish`), "Could not remove")();
  };

  const OWNER_TABS = [["board", "Project Board", LayoutGrid], ["sharing", "Sharing", Share2], ["publish", "Show on profile", Globe2]];
  const SHARED_TABS = [["board", "Board", LayoutGrid], ["comments", "Comments", MessageSquare], ["info", "Shared info", Info]];
  const tabs = isOwner ? OWNER_TABS : SHARED_TABS;

  /* ---------- board canvas (shared by owner + recipient) ---------- */
  const boardCards = (
    <>
      {media.map(m => (
        <PhotoCard key={`m-${m.id}`} m={m} canManage={canContribute} comments={itemComments("media", m.id)}
          onDelete={deleteMedia} onEditCaption={editCaption} canComment={canComment}
          onComment={() => setThread({ ...m, _t: "media" })} />
      ))}
      {pinnedNotes.map(n => (
        <NoteCard key={`n-${n.id}`} n={n} canManage={canContribute} comments={itemComments("note", n.id)}
          onEdit={x => x._unpin ? unpinNote(x) : setNoteDialog(x)} onDelete={deleteNote} canComment={canComment}
          onZoom={setZoom}
          onComment={() => setThread({ ...n, _t: "note" })} />
      ))}
      {pros.map(m => (
        <ProCard key={`p-${m.pro_user_id}`} m={m} canManage={canContribute} comments={itemComments("pro", m.pro_user_id)}
          onStatus={setProStatus} onNote={editProNote} onRemove={removePro} canComment={canComment}
          onComment={() => setThread({ ...m, id: m.pro_user_id, _t: "pro" })} />
      ))}
    </>
  );

  const detailRow = (label, value) => value ? (
    <div className="mb-2.5">
      <p className="text-[11px] uppercase tracking-wider text-[#222]/40 font-semibold">{label}</p>
      <p className="text-[13px] text-[#222]/85 leading-snug">{value}</p>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 bg-black/55 z-50 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose} data-testid="project-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl my-auto flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="project-board">

        {/* header */}
        <div className="px-5 sm:px-7 pt-5 pb-0 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-2xl sm:text-[28px] font-bold flex items-center gap-2.5 truncate" style={{ fontFamily: "Outfit" }} data-testid="project-board-name">
              {project.name}
              <span className="w-2.5 h-2.5 rounded-full bg-[#2E6B57] shrink-0" />
              {project.status === "closed" && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-black/10 text-[#222]/60 shrink-0">In Filing Cabinet</span>}
            </h3>
            <p className="text-[13px] text-[#222]/50 mt-0.5" data-testid="project-board-meta">
              {isOwner
                ? <>{pros.length} professional{pros.length === 1 ? "" : "s"} · {media.length} photo{media.length === 1 ? "" : "s"} · Last updated {relTime(project.updated_at)}</>
                : <>Shared by {data.owner?.name || "a neighbor"} · {itemCount} item{itemCount === 1 ? "" : "s"} · Updated {relTime(project.updated_at)}</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isOwner && (project.status === "active" ? (
              <button onClick={() => setShowClose(true)} title="Move to Filing Cabinet" className="text-xs px-3 sm:px-3.5 py-2 rounded-full border border-black/12 hover:bg-black/5 flex items-center gap-1.5 font-semibold" data-testid="project-close-btn"><Archive size={13} /> <span className="hidden sm:inline">Move to Filing Cabinet</span></button>
            ) : (
              <button onClick={doReopen} title="Reopen project" className="text-xs px-3 sm:px-3.5 py-2 rounded-full border border-black/12 hover:bg-black/5 flex items-center gap-1.5 font-semibold" data-testid="project-reopen-btn"><RotateCcw size={13} /> <span className="hidden sm:inline">Reopen project</span></button>
            ))}
            <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="project-detail-close"><X size={18} /></button>
          </div>
        </div>

        {/* tabs */}
        <div className="flex gap-1 px-5 sm:px-7 mt-3 border-b border-black/[0.07] overflow-x-auto">
          {tabs.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 pb-2.5 pt-1 text-[13.5px] font-semibold shrink-0 border-b-2 flex items-center gap-1.5 transition ${tab === k ? "border-[#2E6B57] text-[#222]" : "border-transparent text-[#222]/45 hover:text-[#222]/70"}`} data-testid={`project-tab-${k}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {showClose && (
          <div className="px-5 sm:px-7 py-4 bg-[#F5F2EB] border-b border-black/5" data-testid="project-close-panel">
            <p className="text-sm font-semibold mb-1">Move "{project.name}" to your Filing Cabinet?</p>
            <p className="text-xs text-[#222]/60 mb-3">You can still access the project, notes, professionals, and saved information later.</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold">Reason:</span>
              <select value={closeReason} onChange={e => setCloseReason(e.target.value)} className="px-2 py-1 rounded-lg border border-black/10 text-xs bg-white" data-testid="project-close-reason">
                {CLOSE_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <div className="ml-auto flex gap-2">
                <button onClick={() => setShowClose(false)} className="text-xs px-3 py-1.5" data-testid="project-close-cancel">Cancel</button>
                <button onClick={doClose} className="text-xs btn-lime px-3 py-1.5" data-testid="project-close-confirm">Move to Filing Cabinet</button>
              </div>
            </div>
          </div>
        )}

        {/* ============ BOARD ============ */}
        {tab === "board" && (
          <div className="flex flex-col lg:flex-row lg:max-h-[74vh] lg:overflow-hidden">
            <div className="lg:w-[72%] flex flex-col lg:overflow-hidden">
              {canContribute ? (
                <div className="px-5 sm:px-7 py-3.5 flex flex-wrap items-center gap-2">
                  <button onClick={() => media.length >= photoLimit ? toast.error(`Projects hold up to ${photoLimit} photos — remove one first.`) : setAddPhoto(true)}
                    className="px-4 py-2.5 rounded-full bg-[#2E6B57] hover:bg-[#245544] text-white text-[13px] font-bold flex items-center gap-1.5 transition" data-testid="board-add-photo">
                    <Plus size={15} /> Add photo
                  </button>
                  <button onClick={() => setShowChoose(true)} className="px-4 py-2.5 rounded-full bg-white border border-black/12 hover:bg-black/[0.03] text-[13px] font-bold flex items-center gap-1.5 transition" data-testid="board-add-pro">
                    <Plus size={15} /> Add pro
                  </button>
                  <button onClick={() => setNoteDialog({})} className="px-4 py-2.5 rounded-full bg-white border border-black/12 hover:bg-black/[0.03] text-[13px] font-bold flex items-center gap-1.5 transition" data-testid="board-add-note">
                    <Plus size={15} /> Add note
                  </button>
                </div>
              ) : (
                <div className="px-5 sm:px-7 py-3.5">
                  <div className="bg-[#EEF3F7] border border-[#CFDCE6] rounded-xl px-4 py-3 flex items-center gap-3" data-testid="shared-permission-banner">
                    <div className="w-9 h-9 rounded-full bg-[#2E6B57] grid place-items-center text-xs font-bold text-white overflow-hidden shrink-0">
                      {data.owner?.avatar_url ? <img src={data.owner.avatar_url} alt="" className="w-full h-full object-cover" /> : (data.owner?.name?.[0] || "?")}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold">This project is shared with you.</p>
                      <p className="text-xs text-[#222]/60">{my_permission === "comment" ? "You can view and comment." : "You can view this project."}</p>
                    </div>
                    <span className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-black/10 shrink-0" data-testid="shared-permission-pill">
                      {PERMISSION_LABEL[my_permission]}
                    </span>
                  </div>
                </div>
              )}

              {/* board canvas — warm paper surface, grows vertically */}
              <div className="flex-1 lg:overflow-y-auto px-5 sm:px-7 pb-6">
                <div className={`rounded-2xl border border-[#E3D9C6] p-4 sm:p-6 ${itemCount === 0 ? "min-h-[240px]" : ""}`} style={{ background: "#F4EBD9" }} data-testid="board-canvas">
                  {itemCount === 0 ? (
                    <div className="py-14 text-center" data-testid="board-empty">
                      <p className="text-sm font-semibold text-[#222]/60">This board is empty.</p>
                      <p className="text-xs text-[#222]/45 mt-1">{canContribute ? "Add a photo, a pro, or a note to start collecting ideas." : "The owner hasn't added anything yet."}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 items-start">
                      {boardCards}
                      {canContribute && (
                        <button onClick={() => setNoteDialog({})} className="rounded-xl border-2 border-dashed border-[#D9CEB6] text-[#8a7c62] hover:border-[#2E6B57] hover:text-[#245544] transition min-h-[124px] grid place-items-center" data-testid="board-add-tile">
                          <span className="text-center px-3">
                            <Plus size={18} className="mx-auto mb-1" />
                            <span className="text-xs font-semibold block leading-tight">Add photo, pro,<br />or note</span>
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {canContribute && media.length >= photoLimit - 2 && (
                  <p className="text-[11px] text-[#222]/45 mt-2" data-testid="board-photo-limit-hint">{media.length} of {photoLimit} photos used.</p>
                )}
              </div>
            </div>

            {/* right sidebar */}
            <div className={`lg:w-[28%] lg:border-l border-t lg:border-t-0 border-black/[0.07] py-6 bg-white flex flex-col lg:overflow-hidden ${isOwner ? "px-5 sm:px-6" : "px-6 sm:px-7"}`} data-testid="project-sidebar">
              <div className="shrink-0" data-testid="project-details-block">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-[15px] font-bold" style={{ fontFamily: "Outfit" }}>{isOwner ? "Project details" : "About this project"}</h4>
                {isOwner && (
                  <button onClick={() => { setForm({ name: project.name, description: project.description, zip: project.zip, timing: project.timing }); setEditDetails(true); }}
                    className="text-xs font-semibold text-[#245544] flex items-center gap-1" data-testid="project-details-edit"><Pencil size={11} /> Edit</button>
                )}
              </div>

              {editDetails ? (
                <div className="space-y-2 mb-4" data-testid="project-details-form">
                  <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Project name"
                    className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="details-name" />
                  <textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Short description"
                    className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="details-description" />
                  <input value={form.zip || ""} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} placeholder="ZIP / neighborhood"
                    className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="details-zip" />
                  <input value={form.timing || ""} onChange={e => setForm(f => ({ ...f, timing: e.target.value }))} placeholder="Desired timing"
                    className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="details-timing" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditDetails(false)} className="text-xs px-3 py-1.5 text-[#222]/60" data-testid="details-cancel">Cancel</button>
                    <button onClick={saveDetails} className="btn-lime px-4 py-1.5 text-xs" data-testid="details-save">Save</button>
                  </div>
                </div>
              ) : (
                <div className="mb-5">
                  {detailRow("Category", (project.categories || []).join(", ") || project.category)}
                  {!isOwner && detailRow("Created by", data.owner?.name)}
                  {detailRow("Location", project.zip)}
                  {detailRow("Status", project.status === "closed" ? "In Filing Cabinet" : "Active")}
                  {detailRow("Timing", project.timing)}
                  {detailRow("Created", project.created_at ? new Date(project.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "")}
                  {detailRow("Description", project.description)}
                </div>
              )}
              </div>

              {isOwner || canContribute ? (
                <div className="border-t border-black/[0.07] pt-4 lg:flex-1 lg:min-h-0 flex flex-col" data-testid="project-notes-block">
                  <NotesSidebar notes={sidebarNotes} canManage={canContribute} isOwner={isOwner}
                    listClass="lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1" onZoom={setZoom}
                    onAdd={() => setNoteDialog({})} onEdit={n => setNoteDialog(n)} onDelete={deleteNote} />
                </div>
              ) : (
                <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto" data-testid="project-notes-block">
                  <div className="border-t border-black/[0.07] pt-5">
                    <NotesSidebar notes={sidebarNotes} canManage={false} isOwner={false} title="Shared notes" onZoom={setZoom}
                      emptyText="The owner hasn't shared any notes." onAdd={() => {}} onEdit={() => {}} onDelete={() => {}} />
                  </div>

                  <div className="border-t border-black/[0.07] pt-5 mt-6" data-testid="shared-activity">
                    <h4 className="text-[13px] font-bold mb-3" style={{ fontFamily: "Outfit" }}>Activity</h4>
                    {comments.length === 0 && <p className="text-xs text-[#222]/40">No comments yet.</p>}
                    {comments.slice(-4).reverse().map(c => (
                      <div key={c.id} className="mb-2.5" data-testid={`activity-${c.id}`}>
                        <p className="text-[12.5px] font-semibold">{c.author_name}</p>
                        <p className="text-xs text-[#222]/65 leading-snug">{c.text}</p>
                        <p className="text-[10px] text-[#222]/40">{relTime(c.created_at)}</p>
                      </div>
                    ))}
                    {canComment && (
                      <button onClick={() => setThread({ id: null, _t: null })} className="w-full mt-2 py-2.5 rounded-xl bg-[#EEF2EF] hover:bg-[#E3EAE5] text-[#245544] text-[13px] font-semibold flex items-center justify-center gap-1.5" data-testid="shared-add-comment">
                        <Plus size={14} /> Add a comment
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============ SHARED: comments tab ============ */}
        {tab === "comments" && (
          <div className="p-5 sm:p-7 max-h-[70vh] overflow-y-auto space-y-3" data-testid="shared-comments-tab">
            {projectComments.length === 0 && <p className="text-sm text-[#222]/40">No comments on this project yet.</p>}
            {projectComments.map(c => (
              <div key={c.id} className="bg-[#F2F4F2] rounded-xl px-4 py-3" data-testid={`project-comment-${c.id}`}>
                <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                <p className="text-[11px] text-[#222]/45 mt-1">{c.author_name} · {relTime(c.created_at)}</p>
              </div>
            ))}
            {canComment ? (
              <button onClick={() => setThread({ id: null, _t: null })} className="btn-lime px-5 py-2.5 text-sm" data-testid="shared-comments-add">Add a comment</button>
            ) : (
              <p className="text-xs text-[#222]/50" data-testid="shared-comments-readonly">You have view-only access — commenting is turned off for you.</p>
            )}
          </div>
        )}

        {/* ============ SHARED: shared info tab ============ */}
        {tab === "info" && (
          <div className="p-5 sm:p-7 max-h-[70vh] overflow-y-auto max-w-lg" data-testid="shared-info-tab">
            {detailRow("Category", (project.categories || []).join(", ") || project.category)}
            {detailRow("Created by", data.owner?.name)}
            {detailRow("Location", project.zip)}
            {detailRow("Timing", project.timing)}
            {detailRow("Description", project.description)}
            {detailRow("Your access", PERMISSION_LABEL[my_permission])}
            <p className="text-xs text-[#222]/45 mt-4">Private notes and the owner's private notes about professionals are never shared.</p>
          </div>
        )}

        {/* ============ OWNER: sharing ============ */}
        {tab === "sharing" && isOwner && (
          <div className="p-5 sm:p-7 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="project-sharing-tab">
            <div className="flex flex-wrap items-center gap-2">
              <select value={shareClient} onChange={e => setShareClient(e.target.value)} className="flex-1 px-3 py-2 border border-black/10 rounded-lg text-sm bg-white min-w-[160px]" data-testid="project-share-client-select">
                <option value="">Share with a Connection…</option>
                {connections.map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
              <select value={sharePerm} onChange={e => setSharePerm(e.target.value)} className="px-3 py-2 border border-black/10 rounded-lg text-sm bg-white" data-testid="project-share-permission-select">
                {PERMISSIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <button onClick={addShare} className="btn-lime px-4 py-2 text-sm" data-testid="project-share-add">Share</button>
            </div>
            <p className="text-xs text-[#222]/55" data-testid="project-share-permission-help">{PERMISSIONS.find(p => p[0] === sharePerm)?.[2]}</p>
            {connections.length === 0 && <p className="text-xs text-[#222]/50">You can only share with an accepted Connection. Visit <a href="/connections" className="underline">Connections</a> to add one.</p>}
            {(shares || []).length === 0 ? (
              <p className="text-sm text-[#222]/40 text-center py-4">Not shared with anyone yet.</p>
            ) : (shares || []).map(s => (
              <div key={s.id} className="flex items-center gap-3 border border-black/10 rounded-xl p-3" data-testid={`project-share-${s.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#2E6B57] grid place-items-center text-xs font-bold text-white overflow-hidden shrink-0">
                  {s.client?.avatar_url ? <img src={s.client.avatar_url} className="w-full h-full object-cover" alt="" /> : s.client?.name?.[0]}
                </div>
                <p className="text-sm font-semibold flex-1 truncate">{s.client?.name}</p>
                <select value={s.permission} onChange={e => updateSharePerm(s.id, e.target.value)} className="text-xs px-2 py-1.5 rounded-lg border border-black/10 bg-white" data-testid={`project-share-permission-${s.id}`}>
                  {PERMISSIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <button onClick={() => revokeShare(s.id)} className="text-[#222]/30 hover:text-red-500" data-testid={`project-share-revoke-${s.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {/* ============ OWNER: show on profile ============ */}
        {tab === "publish" && isOwner && publishDraft && (
          <div className="p-5 sm:p-7 space-y-4 max-h-[70vh] overflow-y-auto max-w-2xl" data-testid="project-publish-tab">
            <p className="text-xs text-[#222]/60">Choose exactly what visitors to your public profile will see. Nothing publishes until you hit Publish.</p>
            <input value={publishForm.title} onChange={e => setPublishForm(f => ({ ...f, title: e.target.value }))} placeholder="Public title" maxLength={80}
              className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="project-publish-title" />
            <textarea value={publishForm.description} onChange={e => setPublishForm(f => ({ ...f, description: e.target.value }))} placeholder="Public description (optional)" rows={2} maxLength={500}
              className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="project-publish-description" />
            <div>
              <p className="text-xs font-semibold mb-1.5">Pros to name publicly</p>
              <div className="flex flex-wrap gap-2">
                {publishDraft.candidate_pros.length === 0 && <p className="text-xs text-[#222]/40">Add pros to this project first.</p>}
                {publishDraft.candidate_pros.map(p => (
                  <button key={p.user_id} onClick={() => togglePub("pro_ids", p.user_id)} className={`px-2.5 py-1.5 rounded-full text-xs border ${publishForm.pro_ids.includes(p.user_id) ? "bg-[#E9F0EC] border-[#2E6B57] text-[#245544]" : "bg-white border-black/10"}`} data-testid={`project-publish-pro-${p.user_id}`}>
                    {p.business_name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold mb-1.5">Photos to publish (only ones you uploaded)</p>
              <div className="flex flex-wrap gap-2">
                {publishDraft.candidate_media.length === 0 && <p className="text-xs text-[#222]/40">Add photos to the Project Board first.</p>}
                {publishDraft.candidate_media.map(m => (
                  <button key={m.id} onClick={() => togglePub("media_ids", m.id)} className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 ${publishForm.media_ids.includes(m.id) ? "border-[#2E6B57]" : "border-transparent"}`} data-testid={`project-publish-media-${m.id}`}>
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              {publishDraft.highlight && <button onClick={doUnpublish} className="px-4 py-2 text-sm text-red-600" data-testid="project-unpublish">Remove from profile</button>}
              <button onClick={doPublish} className="btn-lime px-5 py-2 text-sm" data-testid="project-publish-btn">{publishDraft.highlight ? "Update" : "Publish"}</button>
            </div>
          </div>
        )}
      </div>

      {showChoose && (
        <ChooseFromBoardPanel projectId={projectId} project={project} existingProIds={pros.map(m => m.pro_user_id)} onClose={() => setShowChoose(false)} onAdded={refresh} />
      )}
      {addPhoto && <AddPhotoDialog pros={pros} onCancel={() => setAddPhoto(false)} onSave={savePhoto} />}
      {noteDialog && <NoteDialog note={noteDialog.id ? noteDialog : null} isOwner={isOwner} onCancel={() => setNoteDialog(null)} onSave={saveNote} />}
      {zoom && <ImageLightbox url={zoom} onClose={() => setZoom(null)} />}
      {thread && (
        <CommentThread target={thread} canComment={canComment}
          comments={thread._t ? itemComments(thread._t, thread.id) : projectComments}
          onCancel={() => setThread(null)} onPost={postComment} />
      )}
    </div>
  );
}
